import React, { useEffect, useCallback, useRef } from 'react';
import { connect } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Dispatch, bindActionCreators } from 'redux';
import LifeCheckRecordForm from '../../components/Records/LifeCheck/LifeCheckRecordForm';
import * as RecordActions from '../../actions/Records/actions';
import * as UserActions from '../../actions/User/actions';
import { RootState } from '../../store/configureStore';
import Loading from '../../components/common/Loading';
import { LifeCheckRecord } from '../../constants/Records';

interface RouteParams {
  institutionId: string;
  userId: string;
  sequence: string;
}

interface StateProps {
  institution: any;
  userListData: any[];
  lifecheck: any;
}

interface DispatchProps {
  recordActions: typeof RecordActions;
  userActions: typeof UserActions;
}

type Props = StateProps & DispatchProps;

const LifeCheckRecordEditContainer: React.FC<Props> = ({
  institution,
  userListData,
  lifecheck,
  recordActions,
  userActions,
}) => {
  const { institutionId, userId, sequence } = useParams<RouteParams>();
  const userFetchedRef = useRef(false);
  const recordFetchedRef = useRef(false);

  const user = userListData.find((u: any) => u.user_id === userId);
  const uniqueId = user?.unique_id;

  useEffect(() => {
    if (userListData.length === 0 && !userFetchedRef.current && institutionId) {
      userFetchedRef.current = true;
      userActions.getUsersWithUniqueId(institutionId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [institutionId, userListData.length]);

  useEffect(() => {
    if (uniqueId && sequence && !recordFetchedRef.current) {
      recordFetchedRef.current = true;
      recordActions.fetchLifeCheckRecord(uniqueId, parseInt(sequence, 10));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uniqueId, sequence]);

  const handleFetchRecord = useCallback(
    (id: string, seq: number) => {
      recordActions.fetchLifeCheckRecord(id, seq);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const handleCreate = useCallback(
    (id: string, data: Partial<LifeCheckRecord>) => {
      recordActions.createLifeCheckRecord(id, data);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const handleUpdate = useCallback(
    (id: string, seq: number, data: Partial<LifeCheckRecord>) => {
      recordActions.updateLifeCheckRecord(id, seq, data);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const handleResetFlags = useCallback(() => {
    recordActions.resetLifeCheckFlags();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!user) {
    return <Loading />;
  }

  return (
    <LifeCheckRecordForm
      institutionId={institutionId}
      institutionName={institution?.institution_name || ''}
      userId={userId}
      userName={user.user_name}
      uniqueId={user.unique_id || ''}
      currentRecord={lifecheck.current}
      isEdit={true}
      executing={lifecheck.executing}
      created={lifecheck.created}
      updated={lifecheck.updated}
      onFetchRecord={handleFetchRecord}
      onCreate={handleCreate}
      onUpdate={handleUpdate}
      onResetFlags={handleResetFlags}
    />
  );
};

function mapStateToProps(state: RootState): StateProps {
  return {
    institution: state.institution.institution,
    userListData: state.user.listData,
    lifecheck: state.records.lifecheck,
  };
}

function mapDispatchToProps(dispatch: Dispatch): DispatchProps {
  return {
    recordActions: bindActionCreators(RecordActions, dispatch),
    userActions: bindActionCreators(UserActions, dispatch),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(LifeCheckRecordEditContainer);
